import { Suspense, lazy, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import type { ComponentType } from "react";
import * as React from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "./components/ScrollToTop";
import { QuoteCartProvider } from "./contexts/QuoteCart";
import QuoteDrawer from "./components/QuoteDrawer";
import CookieBanner from "./components/CookieBanner";
import QuickContactPopup from "./components/QuickContactPopup";
import PrerenderSignal from "./components/PrerenderSignal";

// SSG static routes — eager imports so renderToString outputs their markup
// directly (no Suspense boundary walk that would break hydration).
import Index from "./pages/Index.tsx";
import Contact from "./pages/Contact.tsx";
import Borduren from "./pages/Borduren.tsx";
import Configurator from "./pages/Configurator.tsx";
import TShirtBedrukken from "./pages/TShirtBedrukken.tsx";
import TextielBedrukkenPage from "./pages/TextielBedrukkenPage.tsx";
import TShirtBedrukkenWestland from "./pages/TShirtBedrukkenWestland.tsx";
import TShirtBedrukkenZoetermeer from "./pages/TShirtBedrukkenZoetermeer.tsx";
import SportkledingBedrukken from "./pages/SportkledingBedrukken.tsx";
import BedrijfskledingWerkkleding from "./pages/BedrijfskledingWerkkleding.tsx";
import AlgemeneLeveringsvoorwaarden from "./pages/AlgemeneLeveringsvoorwaarden.tsx";
import Blog from "./pages/Blog.tsx";
import ZeefdrukOfTransferdruk from "./pages/ZeefdrukOfTransferdruk.tsx";
import VeiligheidsvestenHesjes from "./pages/VeiligheidsvestenHesjes.tsx";
import TShirtsZomerseizoen from "./pages/TShirtsZomerseizoen.tsx";
import Aanbiedingen from "./pages/Aanbiedingen.tsx";
import DtfPrinting from "./pages/DtfPrinting.tsx";
import Druktechnieken from "./pages/Druktechnieken.tsx";
import Zeefdrukkerij from "./pages/Zeefdrukkerij.tsx";
import Transferdruk from "./pages/Transferdruk.tsx";
import Privacy from "./pages/Privacy.tsx";
import Producten from "./pages/Producten.tsx";
import ProductCategory from "./pages/ProductCategory.tsx";
import TotallyStudentenreizen from "./pages/TotallyStudentenreizen.tsx";
import OverOns from "./pages/OverOns.tsx";
import WerkkledingSchildersStukadoors from "./pages/WerkkledingSchildersStukadoors.tsx";
import TShirtBedrukkenRotterdam from "./pages/TShirtBedrukkenRotterdam.tsx";
import KledingBedrukkenRotterdam from "./pages/KledingBedrukkenRotterdam.tsx";

// Non-indexed / dynamic routes stay lazy.
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const LegacyLandingPage = lazy(() => import("./pages/LegacyLandingPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Beheer = lazy(() => import("./pages/Beheer.tsx"));

const LEGACY_SLUGS = [
  "caps-bedrukken-of-borduren-magic-print-den-haag",
  "t-shirt-bedrukken-voorschoten-magic-print-snel-goedkoop-professioneel",
  "t-shirt-bedrukken-in-leiden",
  "t-shirt-bedrukken-leidschendam-magic-print-leidschendam",
  "shirt-bedrukken-wassenaar",
  "bedankt-voor-je-offerte-aanvraag",
  "t-shirt-bedrukken-zeeland",
  "t-shirt-bedrukken-den-haag",
  "textiel-borduren-rijswijk-magicprint-rijswijk",
  "borduren-den-haag",
  "t-shirt-bedrukken-voorburg-magic-print-voorburg",
  "t-shirt-bedrukken-rijswijk",
  "textiel-borduren-leidschendam-magic-print-leidschendam",
  "textiel-bedrukken",
  "t-shirt-bedrukken-zuid-holland",
  "textiel-borduren-voorburg-magic-print-voorburg",
  "t-shirt-bedrukken-zoetermeer",
  "t-shirt-bedrukken-in-rotterdam",
  "t-shirt-bedrukken-in-delft",
  "t-shirt-bedrukken-scheveningen-magic-print-haag",
  "kleding-bedrukken-rotterdam",
];

const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Sonner injects a <style> tag into <head> on first render. It must not be
// in the SSR output nor the first client hydration render, so we lazy-load
// it only after mount on the client.
const MountedSonner = () => {
  const [SonnerComp, setSonnerComp] = useState<ComponentType | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    import("@/components/ui/sonner").then((m) => {
      if (!cancelled) setSonnerComp(() => m.Toaster as ComponentType);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return SonnerComp ? <SonnerComp /> : null;
};

// Strip trailing slash (legacy magicprint.nl URLs ended with "/")
const TrailingSlashRedirect = () => {
  const { pathname, search, hash } = useLocation();
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return <Navigate to={pathname.replace(/\/+$/, "") + search + hash} replace />;
  }
  return null;
};

const L = (el: React.ReactElement) => (
  <Suspense fallback={<PageFallback />}>{el}</Suspense>
);

/**
 * The full application tree WITHOUT a Router. Wrapped by BrowserRouter on the
 * client and by StaticRouter during SSG. This lets `renderToString` and
 * `hydrateRoot` produce the exact same markup for the same URL.
 */
export const AppTree = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <MountedSonner />
      <QuoteCartProvider>
        <ScrollToTop />
        <TrailingSlashRedirect />
        <QuoteDrawer />
        <CookieBanner />
        <QuickContactPopup />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/offerte" element={<Configurator />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/over-ons" element={<OverOns />} />
          <Route path="/aanbiedingen" element={<Aanbiedingen />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/producten" element={<Producten />} />
          <Route path="/producten/detail/:reference" element={L(<ProductDetail />)} />
          <Route path="/producten/:slug" element={<ProductCategory />} />

          <Route path="/druktechnieken" element={<Druktechnieken />} />
          <Route path="/druktechnieken/borduren" element={<Borduren />} />
          <Route path="/druktechnieken/dtf-printing" element={<DtfPrinting />} />
          <Route path="/druktechnieken/zeefdrukkerij" element={<Zeefdrukkerij />} />
          <Route path="/druktechnieken/transferdruk" element={<Transferdruk />} />
          <Route path="/textiel-bedrukken" element={<TextielBedrukkenPage />} />
          <Route path="/totally-studentenreizen-hoodies-bestellen" element={<TotallyStudentenreizen />} />

          <Route path="/offerte-aanvragen" element={<Navigate to="/offerte" replace />} />
          <Route path="/t-shirt-bedrukken" element={<TShirtBedrukken />} />
          <Route path="/t-shirt-bedrukken-bij-magic-print" element={<Navigate to="/t-shirt-bedrukken" replace />} />
          <Route path="/textiel-laten-bedrukken-bij-magic-print" element={<Navigate to="/textiel-bedrukken" replace />} />
          <Route path="/tshirt-bedrukken-westland" element={<TShirtBedrukkenWestland />} />
          <Route path="/t-shirts-bedrukken-in-zoetermeer" element={<TShirtBedrukkenZoetermeer />} />
          <Route path="/t-shirt-bedrukken-in-rotterdam" element={<TShirtBedrukkenRotterdam />} />
          <Route path="/kleding-bedrukken-rotterdam" element={<KledingBedrukkenRotterdam />} />
          <Route path="/sportkleding-bedrukken-2" element={<SportkledingBedrukken />} />
          <Route path="/bedrijfskleding-werkkleding" element={<BedrijfskledingWerkkleding />} />
          <Route path="/werkkleding-schilders-stukadoors" element={<WerkkledingSchildersStukadoors />} />
          <Route path="/algemene-leveringsvoorwaarden" element={<AlgemeneLeveringsvoorwaarden />} />

          <Route path="/blog" element={<Blog />} />
          <Route path="/zeefdruk-of-transferdruk" element={<ZeefdrukOfTransferdruk />} />
          <Route path="/veiligheidsvesten-veiligheidshesjes" element={<VeiligheidsvestenHesjes />} />
          <Route path="/tshirt-bedrukken" element={<Navigate to="/t-shirt-bedrukken" replace />} />
          <Route path="/t-shirts-bedrukken-voor-het-zomerseizoen" element={<TShirtsZomerseizoen />} />


          {/* Legacy redirects (magicprint.nl 301-map) */}
          <Route path="/sportkleding-bedrukken" element={<Navigate to="/sportkleding-bedrukken-2" replace />} />
          <Route path="/1845-2" element={<Navigate to="/blog" replace />} />
          <Route path="/zomerseizoen-2017" element={<Navigate to="/t-shirts-bedrukken-voor-het-zomerseizoen" replace />} />
          <Route path="/blog-reactive-printing" element={<Navigate to="/blog" replace />} />
          <Route path="/prijsverhogingingen" element={<Navigate to="/blog" replace />} />
          <Route path="/stichting-care-4-the-kids-sint-maarten" element={<Navigate to="/blog" replace />} />
          <Route path="/halen-en-brengen" element={<Navigate to="/blog" replace />} />
          <Route path="/halen-en-brengen-gratis-regio-haag" element={<Navigate to="/contact" replace />} />
          <Route path="/borduren" element={<Navigate to="/druktechnieken/borduren" replace />} />
          <Route path="/textiel-borduren" element={<Navigate to="/druktechnieken/borduren" replace />} />
          <Route path="/druktechnieken/digitaal-drukken-op-textiel" element={<Navigate to="/druktechnieken/dtf-printing" replace />} />
          <Route path="/horecakleding-schorten-sloven-bedrijfskleding" element={<Navigate to="/bedrijfskleding-werkkleding" replace />} />
          <Route path="/category/nieuws" element={<Navigate to="/blog" replace />} />
          <Route path="/category/blog" element={<Navigate to="/blog" replace />} />
          <Route path="/category/textiel-bedrukken" element={<Navigate to="/textiel-bedrukken" replace />} />
          <Route path="/wp-admin" element={<Navigate to="/" replace />} />
          <Route path="/wp-login.php" element={<Navigate to="/" replace />} />
          <Route path="/feed" element={<Navigate to="/" replace />} />

          {LEGACY_SLUGS.filter((s) => s !== "textiel-bedrukken" && s !== "t-shirt-bedrukken-in-rotterdam" && s !== "kleding-bedrukken-rotterdam").map((slug) => (
            <Route key={slug} path={`/${slug}`} element={L(<LegacyLandingPage slug={slug} />)} />
          ))}

          <Route path="/beheer" element={L(<Beheer />)} />
          <Route path="*" element={L(<NotFound />)} />
        </Routes>
        <PrerenderSignal />
      </QuoteCartProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

const App = () => (
  <BrowserRouter>
    <AppTree />
  </BrowserRouter>
);

export default App;
